/**
 * Shared formatters for V3 widgets + landing page.
 */

/**
 * Active display currency for the signed-in client (tenant).
 *
 * Multi-tenant: the currency is the client's own (configured in General Settings
 * → Countries as `country_currency`), NOT a global 'AED'. It's a module-level
 * singleton so all 80+ `formatMoney()` call sites pick it up without threading a
 * prop through every component. Seeded synchronously from localStorage at module
 * load so first paint is already correct on every load after the first login;
 * refreshed by CurrencyBoot (see hooks/useCurrency.js) when the client changes.
 */
let ACTIVE_CURRENCY = (() => {
    try { return localStorage.getItem('displayCurrency') || 'AED'; } catch { return 'AED'; }
})();

export function setActiveCurrency(code) {
    if (code && typeof code === 'string') ACTIVE_CURRENCY = code.trim();
}

export function getActiveCurrency() {
    return ACTIVE_CURRENCY;
}

export function formatMoney(val, currency) {
    if (val === null || val === undefined || isNaN(val)) return '—';
    const cur = currency || ACTIVE_CURRENCY;
    const n = Number(val);
    if (Math.abs(n) >= 1e9) return `${cur} ${(n / 1e9).toFixed(2)}B`;
    if (Math.abs(n) >= 1e6) return `${cur} ${(n / 1e6).toFixed(2)}M`;
    if (Math.abs(n) >= 1e3) return `${cur} ${(n / 1e3).toFixed(2)}K`;
    return `${cur} ${n.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
}

/**
 * Money rendered EXACTLY — full precision, two decimals, comma-grouped, never
 * abbreviated. Use this for every monetary value on an invoice: line amounts,
 * subtotal, tax, total, balance, aging.
 *
 * `formatMoney` abbreviates above 1000 (`AED 1.28M`), which is fine on a KPI
 * tile and indefensible on a document a tenant pays against. It also drops to
 * `maximumFractionDigits: 2` with a MINIMUM of 0, so 1200 renders as "AED 1.2K"
 * and 12 renders as "AED 12" rather than "12.00" — neither is an invoice figure.
 *
 * Takes MAJOR units. Invoice amounts arrive from the API as `*_minor` integers,
 * so divide by 100 (or use formatMinor) before calling.
 */
export function formatMoneyExact(val, currency) {
    if (val === null || val === undefined || isNaN(val)) return '—';
    const cur = currency || ACTIVE_CURRENCY;
    return `${cur} ${Number(val).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    })}`;
}

/** Integer minor units (fils/paise) -> exact money string. */
export function formatMinor(minor, currency) {
    if (minor === null || minor === undefined || isNaN(minor)) return '—';
    return formatMoneyExact(Number(minor) / 100, currency);
}

/** Integer minor units -> bare exact decimal, no currency code. */
export function formatMinorPlain(minor) {
    if (minor === null || minor === undefined || isNaN(minor)) return '—';
    return (Number(minor) / 100).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
}

export function formatNumber(val, decimals = 0) {
    if (val === null || val === undefined || isNaN(val)) return '—';
    return Number(val).toLocaleString('en-US', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
    });
}

export function formatPct(val, decimals = 1) {
    if (val === null || val === undefined || isNaN(val)) return '—';
    const n = Number(val);
    const sign = n > 0 ? '+' : '';
    return `${sign}${n.toFixed(decimals)}%`;
}

export function formatCompactNumber(val) {
    if (val === null || val === undefined || isNaN(val)) return '—';
    const n = Number(val);
    if (Math.abs(n) >= 1e6) return `${(n / 1e6).toFixed(1)}M`;
    if (Math.abs(n) >= 1e3) return `${(n / 1e3).toFixed(1)}K`;
    return n.toFixed(0);
}

/**
 * Full numeric amount with thousands separators and at most 2 decimal places.
 * Use for tooltips, donut totals, and any place that displays a non-abbreviated value.
 */
export function formatAmount(val) {
    if (val === null || val === undefined || isNaN(val)) return '—';
    return Number(val).toLocaleString('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
    });
}
